import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { downloadGeometryFile, GeometryFileParams, GeometryTaskSource, uploadGeometryFile } from "../thunks/geometryFileThunks";

export const pendingGeometriesSliceName = "pendingGeometries";

export interface PendingGeometriesState {
  taskLookup: Record<TaskId, GeometryTask>;
  // geometryId -> number of pending requests for this geometryId
  downloadingGeometryIds: Record<GeometryId, number>;
  // geometryId -> number of pending requests for this geometryId
  uploadingGeometryIds: Record<GeometryId, number>;
}

export interface GeometryTask {
  taskId: TaskId;
  taskType: GeometryTaskType;
  source: GeometryTaskSource;
  createdAt: number;
  // geometryId -> fileName -> status
  status: Record<GeometryId, Record<FileName, GeometryFileStatus>>;
}

type TaskId = string;
type GeometryId = string;
type FileName = string;

export enum GeometryTaskType {
  GEOMETRY_DOWNLOAD = "GEOMETRY_DOWNLOAD",
  GEOMETRY_UPLOAD = "GEOMETRY_UPLOAD",
}

export type GeometryFileStatus =
  | { status: "ERROR"; type: GeometryTaskType; error: unknown }
  | { status: "PENDING"; type: GeometryTaskType }
  | { status: "SUCCESS"; type: GeometryTaskType }
  ;

const initialState: PendingGeometriesState = {
  downloadingGeometryIds: {},
  uploadingGeometryIds: {},
  taskLookup: {},
};

const updateNumberOfPendingRequests = (state: Record<string, number>, geometryId: string, offset: number) => {
  if (geometryId in state && state[geometryId] + offset > 0) state[geometryId] += offset;
  else if (!(geometryId in state) && offset > 0) state[geometryId] = offset;
  // delete the key to exclude it from Object.keys() and Object.entries()
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  else delete state[geometryId];
};

type GeometryFileIdentifier = Omit<GeometryFileParams, "url">;

interface GeometryFileIdentifierPayload {
  taskId: TaskId;
  source: GeometryTaskSource;
}

const getPlaceholderIdentifier = (action: PayloadAction<GeometryFileIdentifierPayload>): GeometryFileIdentifier => {
  return ({ ...action.payload, geometryId: "PLACEHOLDER", fileName: "PLACEHOLDER" });
};

const updateStatusesForGeometryTask = (
  state: PendingGeometriesState["taskLookup"],
  arg: GeometryFileIdentifier,
  geometryStatus: GeometryFileStatus,
  geometryFileNameLookup?: Record<GeometryId, FileName[]>,
) => {
  if (arg.taskId != null) {
    const task: GeometryTask = state[arg.taskId] = state[arg.taskId] ?? {
      taskId: arg.taskId,
      status: {},
      createdAt: new Date().getTime(),
      taskType: geometryStatus.type,
      source: arg.source,
    };
    if (geometryFileNameLookup) {
      Object.entries(geometryFileNameLookup).forEach(([geometryId, fileNames]) => {
        const statusByFile = task.status[geometryId] = task.status[geometryId] ?? {};
        fileNames.forEach((fileName) => {
          statusByFile[fileName] = geometryStatus;
        });
      });
    } else {
      const statusByFile = task.status[arg.geometryId] = task.status[arg.geometryId] ?? {};
      statusByFile[arg.fileName] = geometryStatus;
    }
  }
};

const removeStatusByGeometryTask = (state: PendingGeometriesState["taskLookup"], arg: GeometryFileIdentifier) => {
  if (arg.taskId != null) {
    const task = state[arg.taskId];
    if (task) {
      const statusByFile = task.status[arg.geometryId];
      if (statusByFile[arg.fileName]) {
        // delete the key to exclude it from Object.keys() and Object.entries()
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete statusByFile[arg.fileName];
      }
    }
  }
};

const pendingGeometriesSlice = createSlice({
  name: pendingGeometriesSliceName,
  initialState,
  reducers: {
    clearStatusByGeometryTask(state: PendingGeometriesState, action: PayloadAction<string>) {
      // delete the key to exclude it from Object.keys() and Object.entries()
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.taskLookup[action.payload];
    },
    // methods for mocking a placeholder up/download for long-running tasks with potential pauses during downloads
    addGeometryTaskPlaceholder(state: PendingGeometriesState, action: PayloadAction<GeometryFileIdentifierPayload>) {
      updateStatusesForGeometryTask(state.taskLookup, getPlaceholderIdentifier(action), {
        type: "PLACEHOLDER" as GeometryTaskType,
        status: "PENDING",
      });
    },
    /**
     * method for registering a full download queue against the slice
     *
     * @param state
     * @param action usual params plus record of geometries and their file names
     */
    registerGeometryDownloadQueue(state: PendingGeometriesState, action: PayloadAction<GeometryFileIdentifierPayload & {
      geometryFileNames: Record<GeometryId, FileName[]>;
    }>) {
      updateStatusesForGeometryTask(state.taskLookup, getPlaceholderIdentifier(action), {
        type: "PLACEHOLDER" as GeometryTaskType,
        status: "PENDING",
      }, action.payload.geometryFileNames);
    },
    finishGeometryTask(state: PendingGeometriesState, action: PayloadAction<GeometryFileIdentifierPayload>) {
      removeStatusByGeometryTask(state.taskLookup, getPlaceholderIdentifier(action));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(downloadGeometryFile.pending, (state, action) => {
      updateStatusesForGeometryTask(state.taskLookup, action.meta.arg, { type: GeometryTaskType.GEOMETRY_DOWNLOAD, status: "PENDING" });
      updateNumberOfPendingRequests(state.downloadingGeometryIds, action.meta.arg.geometryId, +1);
    });
    builder.addCase(downloadGeometryFile.fulfilled, (state, action) => {
      updateStatusesForGeometryTask(state.taskLookup, action.meta.arg, { type: GeometryTaskType.GEOMETRY_DOWNLOAD, status: "SUCCESS" });
      updateNumberOfPendingRequests(state.downloadingGeometryIds, action.meta.arg.geometryId, -1);
    });
    builder.addCase(downloadGeometryFile.rejected, (state, action) => {
      updateStatusesForGeometryTask(state.taskLookup, action.meta.arg, { type: GeometryTaskType.GEOMETRY_DOWNLOAD, status: "ERROR", error: action.error });
      updateNumberOfPendingRequests(state.downloadingGeometryIds, action.meta.arg.geometryId, -1);
    });
    builder.addCase(uploadGeometryFile.pending, (state, action) => {
      updateStatusesForGeometryTask(state.taskLookup, action.meta.arg, { type: GeometryTaskType.GEOMETRY_UPLOAD, status: "PENDING" });
      updateNumberOfPendingRequests(state.uploadingGeometryIds, action.meta.arg.geometryId, +1);
    });
    builder.addCase(uploadGeometryFile.fulfilled, (state, action) => {
      updateStatusesForGeometryTask(state.taskLookup, action.meta.arg, { type: GeometryTaskType.GEOMETRY_UPLOAD, status: "SUCCESS" });
      updateNumberOfPendingRequests(state.uploadingGeometryIds, action.meta.arg.geometryId, -1);
    });
    builder.addCase(uploadGeometryFile.rejected, (state, action) => {
      updateStatusesForGeometryTask(state.taskLookup, action.meta.arg, { type: GeometryTaskType.GEOMETRY_UPLOAD, status: "ERROR", error: action.error });
      updateNumberOfPendingRequests(state.uploadingGeometryIds, action.meta.arg.geometryId, -1);
    });
  },
});

export interface PendingGeometriesRootState {
  [pendingGeometriesSliceName]: PendingGeometriesState;
}

export const selectAllPendingGeometryIds = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].downloadingGeometryIds,
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].uploadingGeometryIds,
  (downloadingGeometryIds, uploadingGeometryIds) => {
    return [...Object.keys(downloadingGeometryIds), ...Object.keys(uploadingGeometryIds)];
  },
);

export const selectDownloadingGeometryIds = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].downloadingGeometryIds,
  (downloadingGeometryIds) => Object.keys(downloadingGeometryIds),
);

export const selectUploadingGeometryIds = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].uploadingGeometryIds,
  (uploadingGeometryIds) => Object.keys(uploadingGeometryIds),
);

export const selectGeometryDownloadCount = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].downloadingGeometryIds,
  (downloadingGeometryIds) => Object.values(downloadingGeometryIds).reduce((acc, value) => acc + value, 0),
);

export const selectFinishedGeometryTasks = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].taskLookup,
  (taskLookup) => Object.fromEntries(Object.entries(taskLookup).filter(([, value]) => (
    Object.values(value.status).every((geometry) => Object.values(geometry).every((file) => file.status !== "PENDING"))
  ))),
);

export const selectGeometryTasks = createSelector(
  (state: PendingGeometriesRootState) => state[pendingGeometriesSliceName].taskLookup,
  (taskLookup) => Object.values(taskLookup),
);

export const { clearStatusByGeometryTask, addGeometryTaskPlaceholder, registerGeometryDownloadQueue, finishGeometryTask } = pendingGeometriesSlice.actions;
export const pendingGeometriesReducer = pendingGeometriesSlice.reducer;
